import React, { useState, useEffect, useRef } from 'react';

function Contact() {
    const contactRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        property: '',
        message: '',
        service: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Handle scroll to contact section when navigating from other pages
    useEffect(() => {
        const hash = window.location.hash;
        if (hash === '#contact' && contactRef.current) {
            // Small delay to ensure the component is rendered
            setTimeout(() => {
                contactRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        // Simulate form submission (replace with actual API call)
        setTimeout(() => {
            setSubmitted(true);
            setIsSubmitting(false);
            // Reset form after 3 seconds
            setTimeout(() => {
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    property: '',
                    message: '',
                    service: ''
                });
                setSubmitted(false);
            }, 3000);
        }, 1000);
    };

    return (
        <section id="contact" className="contact-section" ref={contactRef}>
            <div className="section-content">
                <div className="section-header">
                    <div className="section-pretitle">GET IN TOUCH</div>
                    <h2 className="section-title">
                        Ready to Transform Your <span className="highlight">Hotel Operations</span>?
                    </h2>
                    <p className="section-subtitle">
                        Fill out the form below and we'll get back to you within 24 hours. Or call us directly at <a href="tel:+13462661977" className="contact-link">+1 346 266 1977</a>
                    </p>
                </div>

                <div className="contact-container">
                    <div className="contact-info">
                        <div className="contact-info-card">
                            <div className="contact-icon">📧</div>
                            <h3 className="contact-info-title">Email Us</h3>
                            <a href="mailto:info@warpmill.com" className="contact-info-link">
                                info@warpmill.com
                            </a>
                        </div>
                        <div className="contact-info-card">
                            <div className="contact-icon">📞</div>
                            <h3 className="contact-info-title">Call Us</h3>
                            <a href="tel:+13462661977" className="contact-info-link">
                                +1 346 266 1977
                            </a>
                        </div>
                        <div className="contact-info-card">
                            <div className="contact-icon">📍</div>
                            <h3 className="contact-info-title">Location</h3>
                            <p className="contact-info-text">Houston, TX</p>
                        </div>
                    </div>

                    <form className="contact-form" onSubmit={handleSubmit}>
                        {submitted ? (
                            <div className="form-success">
                                <div className="success-icon">✓</div>
                                <h3 className="success-title">Thank You!</h3>
                                <p className="success-message">
                                    We've received your message and will get back to you within 24 hours.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="name" className="form-label">
                                            Full Name <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="form-input"
                                            required
                                            placeholder="John Doe"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="email" className="form-label">
                                            Email Address <span className="required">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="form-input"
                                            required
                                            placeholder="john@hotel.com"
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="phone" className="form-label">
                                            Phone Number <span className="required">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="form-input"
                                            required
                                            placeholder="+1 234 567 8900"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="property" className="form-label">
                                            Property Name
                                        </label>
                                        <input
                                            type="text"
                                            id="property"
                                            name="property"
                                            value={formData.property}
                                            onChange={handleChange}
                                            className="form-input"
                                            placeholder="Grand Hotel"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="service" className="form-label">
                                        Service of Interest
                                    </label>
                                    <select
                                        id="service"
                                        name="service"
                                        value={formData.service}
                                        onChange={handleChange}
                                        className="form-input"
                                    >
                                        <option value="">Select a service...</option>
                                        <option value="customer-care">Customer Care</option>
                                        <option value="ota-management">OTA Management</option>
                                        <option value="social-media">Social Media</option>
                                        <option value="email-marketing">Email Marketing</option>
                                        <option value="software-development">App Development</option>
                                        <option value="recruiting">Recruiting</option>
                                        <option value="all">All Services</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="message" className="form-label">
                                        Message <span className="required">*</span>
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        className="form-input form-textarea"
                                        required
                                        rows="6"
                                        placeholder="Tell us about your hotel and what challenges you're facing..."
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    className="form-submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                </button>
                            </>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
}

export default Contact;

